import { html, LitElement, nothing } from 'lit';
import { productCache, getCurrentMarketContext } from '../utilities/utils.js';
import { repeat } from 'lit/directives/repeat.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { guard } from 'lit/directives/guard.js';
import { keyed } from 'lit/directives/keyed.js';
import wishlist from '../components/wishlist.js';

class WishlistShare extends LitElement {
  static get properties() {
    return {
      products: { type: Array, state: true },
      loading: { type: Boolean, state: true },
      wishlistId: { type: String, state: true },
      discount: { type: Object, attribute: true },
      lastDisplayedCount: { type: Number, state: true },
      displayedProducts: { type: Array, state: true },
      itemsToShow: { type: Number, state: true },
      showLoadMore: { type: Boolean, state: true },
      isLoadingMore: { type: Boolean, state: true },
      hasAttemptedFetch: { type: Boolean, state: true },
      productPositions: { type: Object, state: true },
      renderedProductIds: { type: Object, state: true },
      customerName: { type: String, state: true },
      board: { type: Object, state: true },
      boardId: { type: Number, state: true },
    };
  }

  createRenderRoot() {
    return this;
  }

  constructor() {
    super();
    this.loading = true;
    this.itemsToShow = 20;
    this.displayedProducts = [];
    this.showLoadMore = false;
    this.isLoadingMore = false;
    this.lastDisplayedCount = 0;
    this.hasAttemptedFetch = false;
    this.products = null;
    this.productPositions = new Map();
    this.renderedProductIds = new Set();
    this.ongoingRequests = new Map();
    this.ongoingProductQueries = new Map();
  }

  async connectedCallback() {
    super.connectedCallback();

    if (this.hasAttemptedFetch) {
      return;
    }

    this.loading = true;
    this.lastDisplayedCount = 0;

    this.wishlistId = this.getWishlistId();
    this.boardId = this.getBoardId() || window.wishlist.BOARD_CONFIG.DEFAULT_BOARD_ID;

    if (!this.wishlistId) {
      this.loading = false;
      this.products = [];
      this.hasAttemptedFetch = true;
      return;
    }

    await this.fetchProducts();
  }

  async disconnectedCallback() {
    super.disconnectedCallback();
  }

  getWishlistId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('wishlist_id');
  }

  getBoardId() {
    const urlParams = new URLSearchParams(window.location.search);
    return parseInt(urlParams.get('board_id'));
  }

  async fetchProducts() {
    if (this.hasAttemptedFetch) {
      return;
    }

    this.hasAttemptedFetch = true;

    try {
      if (this.products && this.products.length > 0) {
        this.lastDisplayedCount = this.products.length;
      }

      this.loading = true;

      const region = window.current_region;

      if (!this.wishlistId || !region) {
        throw new Error('Failed to get wishlist');
      }

      const wishlistRequest = await fetch('https://australia-southeast1-foxtware.cloudfunctions.net/shopifyWishlistGet', {
        method: 'POST',
        body: JSON.stringify({
          config: region,
          ...(this.boardId && { boardId: this.boardId }),
        }),
        headers: {
          'Content-Type': 'application/json',
          'x-wf-token': this.wishlistId,
        },
      });

      if (!wishlistRequest.ok) {
        throw new Error('Failed to get wishlist');
      }

      const wishlistResponse = await wishlistRequest.json();

      if (!wishlistResponse.success) {
        throw new Error(wishlistResponse.message);
      }

      const items = wishlistResponse.results?.board?.items || [];
      this.customerName = wishlistResponse.results?.customer?.firstName;
      this.board = wishlistResponse.results?.board || {};
      const products = await this.getProducts(items);

      this.products = products.reverse();
      this.updateDisplayedProducts();
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      this.products = [];
      this.displayedProducts = [];
    } finally {
      this.loading = false;
    }
  }

  async getProducts(ids) {
    const requestKey = `share_${ids.join(',')}_v${Date.now()}`;

    if (this.ongoingRequests?.has(requestKey)) {
      return await this.ongoingRequests.get(requestKey);
    }

    if (!this.ongoingRequests) {
      this.ongoingRequests = new Map();
    }

    const requestPromise = (async () => {
      const cache = productCache();
      const { country } = getCurrentMarketContext();
      let itemsToProcess = ids;

      if (itemsToProcess.length === 0) {
        return [];
      }

      const cachedProducts = new Map();
      const uncachedProductIds = [];
      const dynamicRefreshProductIds = [];

      for (const itemId of itemsToProcess) {
        const { productId } = wishlist.parseWishlistId(itemId);
        const cachedData = await cache.get(productId);
        const updateNeeds = cachedData
          ? cache.needsUpdate(cachedData)
          : { full: true };

        if (cachedData && !updateNeeds.full) {
          cachedProducts.set(productId, cachedData);

          if (updateNeeds.dynamicOnly) {
            dynamicRefreshProductIds.push(productId);
          }
        } else {
          uncachedProductIds.push(productId);
        }
      }

      const fetchedProducts = new Map();

      if (uncachedProductIds.length > 0) {
        const { reusablePromises, newProductIds } = this.separateQueriesFromOngoing?.(uncachedProductIds) ||
          { reusablePromises: new Map(), newProductIds: uncachedProductIds };

        for (const [productId, promise] of reusablePromises) {
          try {
            const result = await promise;
            if (result) {
              fetchedProducts.set(productId, result);
            }
          } catch (error) {
            console.error(`Error waiting for ongoing query for product ${productId}:`, error);
          }
        }

        if (newProductIds.length > 0) {
          const batches = [];
          for (let i = 0; i < newProductIds.length; i += 20) {
            batches.push(newProductIds.slice(i, i + 20));
          }

          const unpublishedProductIds = [];

          for (const batch of batches) {
            const batchPromise = (async () => {
              try {
                const productIds = batch.map((id) => `gid://shopify/Product/${id}`);

                const response = await fetch(wishlist.STOREFRONT_API_URL, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'X-Shopify-Storefront-Access-Token': wishlist.STOREFRONT_ACCESS_TOKEN,
                  },
                  body: JSON.stringify({
                    query: wishlist.PRODUCTS_BULK_QUERY,
                    variables: {
                      ids: productIds,
                      country,
                    },
                  }),
                });

                console.log(response)

                if (!response.ok) {
                  console.warn(`Failed to fetch batch: ${response.status} ${response.statusText}`);
                  return new Map();
                }

                const fetchedData = await response.json();

                if (fetchedData.errors) {
                  console.warn('GraphQL errors in bulk fetch:', fetchedData.errors);
                }

                const batchResults = new Map();

                if (fetchedData.data?.nodes) {
                  for (const product of fetchedData.data.nodes) {
                    if (product && product.id) {
                      const productId = product.id.replace('gid://shopify/Product/', '');

                      if (!product.onlineStoreUrl) {
                        console.warn(`Product ${productId} is not published to online store (onlineStoreUrl is null), excluding from wishlist`);
                        unpublishedProductIds.push(productId);
                        continue;
                      }

                      const convertedProductData = wishlist.convertGraphQLProductToLiquidProduct(product);
                      batchResults.set(productId, convertedProductData);

                      // Always use 'full' update type for newly fetched products
                      await cache.set(productId, convertedProductData, 'full');
                    }
                  }
                }

                return batchResults;
              } catch (error) {
                console.error('Error in bulk fetch batch:', error);
                return new Map();
              }
            })();

            if (!this.ongoingProductQueries) {
              this.ongoingProductQueries = new Map();
            }

            batch.forEach((productId) => {
              const productPromise = batchPromise.then((batchResults) => batchResults.get(productId));
              this.ongoingProductQueries.set(productId, productPromise);
            });

            try {
              const batchResults = await batchPromise;
              for (const [productId, result] of batchResults) {
                fetchedProducts.set(productId, result);
              }
            } catch (error) {
              console.error('Error in batch processing:', error);
            } finally {
              batch.forEach((productId) => {
                this.ongoingProductQueries.delete(productId);
              });
            }
          }

          if (unpublishedProductIds.length > 0 && wishlist.remove) {
            await wishlist.remove(unpublishedProductIds);
          }
        }
      }

      // Trigger dynamic refresh for products that need it
      if (dynamicRefreshProductIds.length > 0) {
        wishlist
          .refreshDynamicProductData?.(dynamicRefreshProductIds)
          ?.catch?.((error) => {
            console.error('Error refreshing dynamic product data:', error);
          });
      }

      const results = [];

      for (const itemId of itemsToProcess) {
        try {
          const { productId, variantId } = wishlist.parseWishlistId(itemId);

          const productData = cachedProducts.get(productId) || fetchedProducts.get(productId);

          if (!productData) {
            continue; // Skip products we couldn't fetch
          }

          const product = productData.product;
          const siblings = productData.siblings || [];

          if (!product) continue;

          if (!product.url) {
            console.warn(`Product ${productId} has no URL after conversion, excluding from wishlist`);
            continue;
          }

          const selectedVariant = product?.variants?.find((variant) => String(variant.id) === String(variantId)) || null;

          results.push({
            product,
            siblings,
            itemId,
            selectedVariant,
          });
        } catch (error) {
          console.error('Error processing product:', error);
          continue;
        }
      }

      return results;
    })();

    this.ongoingRequests.set(requestKey, requestPromise);

    try {
      return await requestPromise;
    } finally {
      this.ongoingRequests.delete(requestKey);
    }
  }

  separateQueriesFromOngoing(productIds) {
    if (!this.ongoingProductQueries) {
      this.ongoingProductQueries = new Map();
    }

    const reusablePromises = new Map();
    const newProductIds = [];

    for (const productId of productIds) {
      if (this.ongoingProductQueries.has(productId)) {
        reusablePromises.set(productId, this.ongoingProductQueries.get(productId));
      } else {
        newProductIds.push(productId);
      }
    }

    return { reusablePromises, newProductIds };
  }

  updateDisplayedProducts() {
    if (!this.products) return;

    const previousDisplayedProducts = this.displayedProducts || [];
    this.displayedProducts = this.products.slice(0, this.itemsToShow);

    const currentProductIds = new Set();
    this.displayedProducts.forEach((product, index) => {
      const productId = product.product.id;
      currentProductIds.add(productId);
      const wasAlreadyDisplayed = previousDisplayedProducts.some((prev) => prev.product.id === productId);

      if (!this.productPositions.has(productId) || !wasAlreadyDisplayed) {
        this.productPositions.set(productId, index);
      }
    });

    for (const [productId] of this.productPositions) {
      if (!currentProductIds.has(productId)) {
        this.productPositions.delete(productId);
        this.renderedProductIds.delete(productId);
      }
    }

    if (!this.loading && this.displayedProducts.length > 0) {
      this.lastDisplayedCount = this.displayedProducts.length;
    }

    const hasMoreProducts = this.products.length > this.itemsToShow;
    const hasProductsToShow = this.products.length > this.displayedProducts.length;

    this.showLoadMore = hasMoreProducts && hasProductsToShow;

    if (this.products.length < this.itemsToShow && this.products.length > 0) {
      this.itemsToShow = Math.max(20, this.products.length);
    }
  }

  loadMore() {
    if (this.isLoadingMore || !this.showLoadMore) return;

    this.isLoadingMore = true;

    this.itemsToShow = this.itemsToShow + 20;

    this.updateDisplayedProducts();
    this.isLoadingMore = false;
  }

  renderEmoji(emoji) {
    const emojiObj = window.wishlist_emojis.find((e) => e.value === emoji) || window.wishlist_emojis.find((e) => e.default);

    if (!emojiObj) return nothing;

    if (emojiObj.type === 'svg') {
      return html`${unsafeHTML(emojiObj.display)}`;
    }

    if (emojiObj.type === 'image') {
      return html`<img src="${emojiObj.display}" width="24" height="24" alt="${emojiObj.value}" loading="lazy" />`;
    }

    if (emojiObj.type === 'emoji') {
      return html`<span>${unsafeHTML(emojiObj.display)}</span>`;
    }
  }

  renderTitle() {
    const colour = window.wishlist_colours.find((c) => c.value === this.board?.colour)?.colour || window.wishlist_colours.find((c) => c.default)?.colour || this.board?.colour || 'white';

    return html`
      <div
        class="wishlist_board_banner ${this.board
        ? ''
        : 'wishlist_board_banner_skelly'}"
        style="--wishlist-colour: ${this.board
        ? colour
        : '#e9e9e9'};"
      >
        <h1
          style="${this.board
        ? ''
        : 'color: rgba(0,0,0,0);'}"
        >
          ${this.renderEmoji(this.board?.emoji)} ${this.board?.name || 'Board'}
        </h1>
        <span class="wishlist_shared_by">${unsafeHTML(window.theme.strings.wishlist.shared_by.replace('{{ customer_name }}', this.customerName))}</span>
      </div>
    `;
  }

  render() {
    if (this.loading || this.products === null) {
      return html`
        ${this.renderTitle()}
        <div class="wishlist_container_wrapper">
          <ul class="wishlist_container">
            ${this._renderWishlistLoading()}
          </ul>
        </div>
      `;
    }

    return html`
      ${this.renderTitle()}
      ${this.products && this.products.length > 0
        ? html`<div class="wishlist_container_wrapper">
            <ul class="wishlist_container">
              ${this._renderWishlistContent()}
            </ul>
            ${this.showLoadMore
            ? this._renderLoadMoreButton()
            : ''}
          </div>`
        : this._renderWishlistEmpty()}
    `;
  }

  _renderWishlistLoading() {
    const lastCount = this.lastDisplayedCount || 0;
    const plannedCount = 8;
    const skeletonCount = Math.max(lastCount, plannedCount);

    return html`${repeat([...Array(skeletonCount)], () => html`<li><product-card-skeleton swatches="false"></product-card-skeleton></li>`)}`;
  }

  _renderWishlistEmpty() {
    return html` <div
      class="wishlist_empty_wrapper wishlist_loading ${!this.loading
        ? 'wishlist_loaded'
        : ''}"
    >
      <h1 class="wishlist_empty_title">${unsafeHTML(window.theme.strings.wishlist_share.no_item_title)}</h1>
    </div>`;
  }

  _renderWishlistContent() {
    const products = this.displayedProducts || [];
    const productItems = repeat(
      products,
      (item) => item.product.id,
      (item, index) => {
        const productId = item.product.id;
        const stablePosition = this.productPositions.get(productId) || index;
        const isFirstRender = !this.renderedProductIds.has(productId);

        this.renderedProductIds.add(productId);

        const guardDeps = [productId, this.discount];

        return keyed(
          productId,
          guard(guardDeps, () => {
            const animationStyle = isFirstRender
              ? `animation-delay: ${(stablePosition % 20) * 0.08}s;`
              : '';

            return html`
              <li class="wishlist_item" style="${animationStyle}">
                <product-card
                  .discount=${this.discount}
                  .product=${item.product}
                  .siblings=${item.siblings}
                  .initialSelectedVariant=${item.selectedVariant}
                  swatches="false"
                  mode="variant-select"
                  loading=${index < 8
                ? 'eager'
                : 'lazy'}
                ></product-card>
              </li>
            `;
          }),
        );
      },
    );

    const loadingSkeletons =
      this.isLoadingMore && this.showLoadMore
        ? this._renderNextBatchSkeletons()
        : '';

    return html`${productItems}${loadingSkeletons}`;
  }

  _renderNextBatchSkeletons() {
    const nextBatchSize = Math.min(20, this.products?.length - this.displayedProducts?.length || 0);
    if (nextBatchSize <= 0) return '';

    const currentDisplayedCount = this.displayedProducts?.length || 0;

    return html`${repeat(
      [...Array(nextBatchSize)],
      (_, index) => `loading-${currentDisplayedCount + index}`,
      (_, index) => {
        const skeletonPosition = currentDisplayedCount + index;
        return html`
          <li key="loading-${skeletonPosition}" class="wishlist_item_loading" style="animation-delay: ${(skeletonPosition % 20) * 0.08}s;">
            <product-card-skeleton swatches="false"></product-card-skeleton>
          </li>
        `;
      },
    )}`;
  }

  _renderLoadMoreButton() {
    return html`
      <div class="wishlist_load_more_container">
        <button class="wishlist_load_more_btn btn btn-primary" @click=${this.loadMore} ?disabled=${this.isLoadingMore}>
          ${this.isLoadingMore
        ? html`${unsafeHTML(window.theme.strings.wishlist.loading)}`
        : html`${unsafeHTML(window.theme.strings.wishlist.load_more)}`}
        </button>
      </div>
    `;
  }
}

window.customElements.get('wishlist-share') || window.customElements.define('wishlist-share', WishlistShare);
